/**
 * Announcements Dashboard
 *
 * Main dashboard for Announcement Management domain.
 * Shows real-time stats, quick actions, recent announcements,
 * upcoming announcements, and draft/pending items.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconSpeakerphone,
  IconCheck,
  IconStar,
  IconClock,
  IconArrowRight,
  IconRefresh,
  IconSettings,
  IconList,
  IconPlus,
  IconCalendarEvent,
  IconFileDescription,
} from '@tabler/icons-react';
import {
  Stack,
  Card,
  Grid,
  Group,
  Badge,
  Button,
  Text,
  Title,
  ThemeIcon,
  Paper,
  Skeleton,
  Table,
  SimpleGrid,
  Alert,
  Image,
} from '@/components/ui';
import { FadeIn, HoverLift, ScrollReveal } from '@/components/animations';
import { useAnnouncementService } from '@actschurch/ccms-sdk';

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString();
};

export default function AnnouncementsDashboard() {
  const navigate = useNavigate();
  const announcementService = useAnnouncementService();
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const { data: announcements = [], isLoading, error } = useQuery({
    queryKey: ['announcements', 'views'],
    queryFn: () => announcementService.getViews(),
  });

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await queryClient.invalidateQueries({ queryKey: ['announcements'] });
    setLastRefresh(new Date());
    setIsRefreshing(false);
  };

  const formatLastRefresh = (date: Date | null) => {
    if (!date) return null;
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Calculate stats
  const stats = useMemo(() => {
    const now = new Date();
    const active = announcements.filter(
      (a) => a.statusID === 1 && new Date(a.activeFromDate) <= now && (!a.activeToDate || new Date(a.activeToDate) > now)
    ).length;
    const featured = announcements.filter((a) => a.isFeatured).length;
    const scheduled = announcements.filter((a) => new Date(a.activeFromDate) > now).length;

    return { total: announcements.length, active, featured, scheduled };
  }, [announcements]);

  // Recent announcements (latest 5)
  const recentAnnouncements = useMemo(() => {
    return [...announcements]
      .sort((a, b) => b.createdDateTime - a.createdDateTime)
      .slice(0, 5);
  }, [announcements]);

  // Upcoming announcements (next 5 by activeFromDate)
  const upcomingAnnouncements = useMemo(() => {
    const now = new Date();
    return [...announcements]
      .filter((a) => new Date(a.activeFromDate) > now)
      .sort((a, b) => new Date(a.activeFromDate).getTime() - new Date(b.activeFromDate).getTime())
      .slice(0, 5);
  }, [announcements]);

  // Draft/Pending announcements
  const draftAnnouncements = useMemo(() => {
    return announcements
      .filter((a) => a.statusName?.toLowerCase().includes('draft') || a.statusName?.toLowerCase().includes('pending'))
      .slice(0, 5);
  }, [announcements]);

  if (isLoading) {
    return (
      <Stack gap="lg">
        <Skeleton height={40} width={300} />
        <SimpleGrid cols={{ base: 2, md: 4 }}>
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} height={120} />)}
        </SimpleGrid>
        <SimpleGrid cols={{ base: 1, md: 2 }}>
          <Skeleton height={300} />
          <Skeleton height={300} />
        </SimpleGrid>
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack gap="lg">
        <Title order={2}>Announcement Management</Title>
        <Alert color="red" title="Service Unavailable">
          <Text size="sm">
            {error instanceof Error ? error.message : 'Failed to load announcements. The service may be unavailable.'}
          </Text>
          <Button variant="light" color="red" size="xs" mt="sm" onClick={handleRefresh} loading={isRefreshing}>
            Retry
          </Button>
        </Alert>
      </Stack>
    );
  }

  const statCards = [
    { label: 'Total Announcements', value: stats.total, icon: IconSpeakerphone, color: 'blue' },
    { label: 'Active', value: stats.active, icon: IconCheck, color: 'green' },
    { label: 'Featured', value: stats.featured, icon: IconStar, color: 'yellow' },
    { label: 'Scheduled', value: stats.scheduled, icon: IconClock, color: 'violet' },
  ];

  return (
    <FadeIn>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <div>
            <Title order={2}>Announcement Management</Title>
            <Text size="sm" c="dimmed">Overview of announcements, locations, events, and more</Text>
          </div>
          <Group gap="sm" align="center">
            {lastRefresh && (
              <Text size="xs" c="dimmed">Last updated: {formatLastRefresh(lastRefresh)}</Text>
            )}
            <Button variant="subtle" leftSection={<IconRefresh size={16} />} onClick={handleRefresh} loading={isRefreshing} size="sm">
              Refresh
            </Button>
            <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/app/announcements/new')} size="sm">
              New Announcement
            </Button>
          </Group>
        </Group>

        {/* Stats Grid */}
        <SimpleGrid cols={{ base: 2, md: 4 }}>
          {statCards.map((stat) => (
            <HoverLift key={stat.label} lift={4}>
              <Paper
                withBorder
                p="md"
                radius="md"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/app/announcements/list')}
              >
                <Group justify="space-between" mb="xs">
                  <ThemeIcon size="lg" radius="md" variant="light" color={stat.color}>
                    <stat.icon size={20} />
                  </ThemeIcon>
                </Group>
                <Text size="xl" fw={700}>{stat.value}</Text>
                <Text size="sm" c="dimmed">{stat.label}</Text>
              </Paper>
            </HoverLift>
          ))}
        </SimpleGrid>

        <Grid>
          {/* Recent Announcements */}
          <Grid.Col span={{ base: 12, lg: 6 }}>
            <ScrollReveal>
              <Card withBorder padding="md" radius="md" h="100%">
                <Group justify="space-between" mb="md">
                  <Group gap="sm">
                    <ThemeIcon size="md" radius="md" variant="light" color="blue">
                      <IconSpeakerphone size={16} />
                    </ThemeIcon>
                    <Text fw={600}>Recent Announcements</Text>
                  </Group>
                  <Button variant="subtle" size="xs" rightSection={<IconArrowRight size={14} />} onClick={() => navigate('/app/announcements/list')}>
                    View All
                  </Button>
                </Group>

                {recentAnnouncements.length === 0 ? (
                  <Text c="dimmed" size="sm" ta="center" py="lg">No announcements yet</Text>
                ) : (
                  <Table>
                    <Table.Tbody>
                      {recentAnnouncements.map((ann) => (
                        <Table.Tr key={ann.announcementID} style={{ cursor: 'pointer' }} onClick={() => navigate(`/app/announcements/view/${ann.announcementID}`)}>
                          <Table.Td>
                            <Group gap="sm" wrap="nowrap">
                              {ann.thumbnailUrl && <Image src={ann.thumbnailUrl} w={36} h={36} fit="cover" radius="sm" />}
                              <div>
                                <Text size="sm" fw={500} lineClamp={1}>{ann.title || `Announcement #${ann.announcementID}`}</Text>
                                <Text size="xs" c="dimmed" lineClamp={1}>{ann.typeName}</Text>
                              </div>
                            </Group>
                          </Table.Td>
                          <Table.Td>
                            {ann.statusName && (
                              <Badge size="xs" variant="light" color={ann.statusColor || 'gray'}>{ann.statusName}</Badge>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
              </Card>
            </ScrollReveal>
          </Grid.Col>

          {/* Upcoming Announcements */}
          <Grid.Col span={{ base: 12, lg: 6 }}>
            <ScrollReveal>
              <Card withBorder padding="md" radius="md" h="100%">
                <Group justify="space-between" mb="md">
                  <Group gap="sm">
                    <ThemeIcon size="md" radius="md" variant="light" color="violet">
                      <IconCalendarEvent size={16} />
                    </ThemeIcon>
                    <Text fw={600}>Upcoming Announcements</Text>
                  </Group>
                  <Badge size="sm" variant="light" color="violet">{upcomingAnnouncements.length}</Badge>
                </Group>

                {upcomingAnnouncements.length === 0 ? (
                  <Text c="dimmed" size="sm" ta="center" py="lg">No upcoming announcements</Text>
                ) : (
                  <Stack gap="xs">
                    {upcomingAnnouncements.map((ann) => (
                      <HoverLift key={ann.announcementID} lift={2}>
                        <Paper withBorder p="sm" radius="sm" style={{ cursor: 'pointer' }} onClick={() => navigate(`/app/announcements/view/${ann.announcementID}`)}>
                          <Group justify="space-between" wrap="nowrap">
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <Text size="sm" fw={500} lineClamp={1}>{ann.title || `Announcement #${ann.announcementID}`}</Text>
                              <Text size="xs" c="dimmed">Starts {formatDate(ann.activeFromDate)}</Text>
                            </div>
                            {ann.priorityName && (
                              <Badge size="xs" variant="light" color={ann.priorityColor || 'gray'}>{ann.priorityName}</Badge>
                            )}
                          </Group>
                        </Paper>
                      </HoverLift>
                    ))}
                  </Stack>
                )}
              </Card>
            </ScrollReveal>
          </Grid.Col>
        </Grid>

        <Grid>
          {/* Drafts / Pending */}
          <Grid.Col span={{ base: 12, lg: 6 }}>
            <ScrollReveal>
              <Card withBorder padding="md" radius="md" h="100%">
                <Group justify="space-between" mb="md">
                  <Group gap="sm">
                    <ThemeIcon size="md" radius="md" variant="light" color="orange">
                      <IconFileDescription size={16} />
                    </ThemeIcon>
                    <Text fw={600}>Drafts & Pending</Text>
                  </Group>
                  <Badge size="sm" variant="light" color="orange">{draftAnnouncements.length}</Badge>
                </Group>

                {draftAnnouncements.length === 0 ? (
                  <Text c="dimmed" size="sm" ta="center" py="lg">No drafts or pending announcements</Text>
                ) : (
                  <Stack gap="xs">
                    {draftAnnouncements.map((ann) => (
                      <HoverLift key={ann.announcementID} lift={2}>
                        <Paper withBorder p="sm" radius="sm" style={{ cursor: 'pointer' }} onClick={() => navigate(`/app/announcements/view/${ann.announcementID}`)}>
                          <Group justify="space-between" wrap="nowrap">
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <Text size="sm" fw={500} lineClamp={1}>{ann.title || `Announcement #${ann.announcementID}`}</Text>
                              <Text size="xs" c="dimmed">{ann.typeName}</Text>
                            </div>
                            <Badge size="xs" variant="light" color={ann.statusColor || 'gray'}>{ann.statusName}</Badge>
                          </Group>
                        </Paper>
                      </HoverLift>
                    ))}
                  </Stack>
                )}
              </Card>
            </ScrollReveal>
          </Grid.Col>

          {/* Quick Actions */}
          <Grid.Col span={{ base: 12, lg: 6 }}>
            <ScrollReveal>
              <Card withBorder padding="md" radius="md" h="100%">
                <Group gap="sm" mb="md">
                  <ThemeIcon size="md" radius="md" variant="light" color="teal">
                    <IconArrowRight size={16} />
                  </ThemeIcon>
                  <Text fw={600}>Quick Actions</Text>
                </Group>

                <Stack gap="sm">
                  <HoverLift lift={2}>
                    <Paper withBorder p="sm" radius="sm" style={{ cursor: 'pointer' }} onClick={() => navigate('/app/announcements/new')}>
                      <Group gap="sm">
                        <IconPlus size={16} color="var(--mantine-color-blue-6)" />
                        <div>
                          <Text size="sm" fw={500}>Create Announcement</Text>
                          <Text size="xs" c="dimmed">Start a new announcement</Text>
                        </div>
                      </Group>
                    </Paper>
                  </HoverLift>
                  <HoverLift lift={2}>
                    <Paper withBorder p="sm" radius="sm" style={{ cursor: 'pointer' }} onClick={() => navigate('/app/announcements/list')}>
                      <Group gap="sm">
                        <IconList size={16} color="var(--mantine-color-green-6)" />
                        <div>
                          <Text size="sm" fw={500}>Manage Announcements</Text>
                          <Text size="xs" c="dimmed">View, edit, and delete announcements</Text>
                        </div>
                      </Group>
                    </Paper>
                  </HoverLift>
                  <HoverLift lift={2}>
                    <Paper withBorder p="sm" radius="sm" style={{ cursor: 'pointer' }} onClick={() => navigate('/app/announcements/analytics')}>
                      <Group gap="sm">
                        <IconSpeakerphone size={16} color="var(--mantine-color-orange-6)" />
                        <div>
                          <Text size="sm" fw={500}>View Analytics</Text>
                          <Text size="xs" c="dimmed">Views and clicks across all announcements</Text>
                        </div>
                      </Group>
                    </Paper>
                  </HoverLift>
                  <HoverLift lift={2}>
                    <Paper withBorder p="sm" radius="sm" style={{ cursor: 'pointer' }} onClick={() => navigate('/app/announcements/admin/lookups')}>
                      <Group gap="sm">
                        <IconSettings size={16} color="var(--mantine-color-violet-6)" />
                        <div>
                          <Text size="sm" fw={500}>Manage Lookups</Text>
                          <Text size="xs" c="dimmed">Statuses, types, categories, priorities</Text>
                        </div>
                      </Group>
                    </Paper>
                  </HoverLift>
                </Stack>
              </Card>
            </ScrollReveal>
          </Grid.Col>
        </Grid>
      </Stack>
    </FadeIn>
  );
}
